import React, { useState, useEffect } from 'react';
import axios from 'axios';
import SiteMap from './SiteMap';
import HistoryTab from './HistoryTab';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box,
    Tabs, Tab, Typography, Chip, Paper
} from '@mui/material';
import { X, DollarSign, MapPin, Calendar, User } from 'lucide-react';
import { format } from 'date-fns';

function TabPanel({ children, value, index }) {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && children}
        </div>
    );
}

function formatCurrency(value) {
    const num = Number(value ?? 0);
    return `$${num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

function QuoteDetailsModal({ open, onClose, quote }) {
    const [tabValue, setTabValue] = useState(0);
    const [siteLatLng, setSiteLatLng] = useState(null);

    useEffect(() => {
        if (open && quote?.Site?.Name) {
            const fetchLatLng = async () => {
                try {
                    const response = await axios.get('https://nominatim.openstreetmap.org/search', {
                        params: {
                            q: `${quote.Site.Name} VIC Australia`,
                            format: 'json',
                            limit: 1
                        }
                    });
                    if (response.data?.length > 0) {
                        const { lat, lon } = response.data[0];
                        setSiteLatLng({ lat: parseFloat(lat), lng: parseFloat(lon) });
                    }
                } catch (error) {
                    console.error('Failed to fetch geocode:', error);
                    setSiteLatLng(null);
                }
            };
            fetchLatLng();
        }
    }, [open, quote]);

    if (!open || !quote) return null;

    const sections = quote?.Sections || [];
    const customerName = quote.Customer?.CompanyName || `${quote.Customer?.GivenName ?? ''} ${quote.Customer?.FamilyName ?? ''}`.trim() || 'Unknown Customer';
    const salesperson = quote.Salesperson?.Name || 'Not assigned';
    const siteName = quote.Site?.Name || 'Not specified';
    const suburb = siteName.includes(',') ? siteName.split(',').pop()?.trim() : siteName;
    const formattedPrice = quote.Total?.IncTax ? formatCurrency(quote.Total.IncTax) : 'N/A';
    const formattedDate = quote.DateIssued ? format(new Date(quote.DateIssued), 'MMMM d, yyyy') : 'N/A';

    const handleChangeTab = (event, newValue) => {
        setTabValue(newValue);
    };

    return (
        <Dialog open={open} onClose={onClose} keepMounted maxWidth="md" fullWidth PaperProps={{ style: { backgroundColor: '#121212', color: '#e0e0e0' } }}>
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontFamily: 'monospace', color: '#61dafb' }}>{quote.ID}</Typography>
                    {quote.Status?.Name && <Chip label={quote.Status.Name} size="small" sx={{ bgcolor: '#333', color: 'white' }} />}
                </Box>
                <Button onClick={onClose} sx={{ minWidth: 'auto' }}><X size={20} /></Button>
            </DialogTitle>

            <Tabs value={tabValue} onChange={handleChangeTab} centered>
                <Tab label="Overview" />
                <Tab label="Details" />
                <Tab label="Line Items" />
                <Tab label="Notes" />
                <Tab label="History" />
            </Tabs>

            <DialogContent>
                <TabPanel value={tabValue} index={0}>
                    <Typography variant="h5" gutterBottom>{customerName}</Typography>
                    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#1e1e1e' }}>
                        <Typography variant="subtitle1" gutterBottom fontWeight="bold">Key Information</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <DollarSign size={18} />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Total Price</Typography>
                                    <Typography variant="body1">{formattedPrice}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Calendar size={18} />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Date Issued</Typography>
                                    <Typography variant="body1">{formattedDate}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <MapPin size={18} />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Site Location</Typography>
                                    <Typography variant="body1">{siteName}</Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <User size={18} />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Salesperson</Typography>
                                    <Typography variant="body1">{salesperson}</Typography>
                                </Box>
                            </Box>
                        </Box>
                        {siteLatLng && (
                            <Box sx={{ mt: 2 }}>
                                <SiteMap siteLat={siteLatLng.lat} siteLng={siteLatLng.lng} siteName={siteName} />
                            </Box>
                        )}
                    </Paper>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                    {quote.Description ? (
                        <Box sx={{ p: 2, bgcolor: '#1e1e1e', borderRadius: 1 }} dangerouslySetInnerHTML={{ __html: quote.Description }} />
                    ) : (
                        <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>No description available</Typography>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={2}>
                    {sections.length > 0 ? (
                        sections.map((section, sectionIdx) => (
                            <Box key={sectionIdx} sx={{ mb: 4 }}>
                                {section.Name && <Typography variant="h6" gutterBottom>{section.Name}</Typography>}
                                {section.CostCenters?.map((costCenter, ccIdx) => (
                                    <Box key={ccIdx} sx={{ mb: 3 }}>
                                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{costCenter.Name || 'Unnamed Cost Center'}</Typography>
                                        <Paper variant="outlined" sx={{ mt: 1, bgcolor: '#1e1e1e', border: '1px solid rgba(255,255,255,0.1)', overflowX: 'auto' }}>
                                            <Box sx={{ minWidth: 700 }}>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: '4fr 1fr 2fr 2fr', p: 1, bgcolor: 'rgba(0,0,0,0.2)', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                                                    <Typography fontWeight="bold">Item</Typography>
                                                    <Typography fontWeight="bold" align="center">Qty</Typography>
                                                    <Typography fontWeight="bold" align="right">Unit Price</Typography>
                                                    <Typography fontWeight="bold" align="right">Total</Typography>
                                                </Box>
                                                {costCenter.Items?.Prebuilds?.map((item, itemIdx) => (
                                                    <Box key={itemIdx} sx={{ display: 'grid', gridTemplateColumns: '4fr 1fr 2fr 2fr', p: 1, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                        <Typography>{item.Prebuild?.Name || 'Unnamed Item'}</Typography>
                                                        <Typography align="center">{item.Total?.Qty ?? 0}</Typography>
                                                        <Typography align="right">{formatCurrency(item.SellPrice?.ExTax)}</Typography>
                                                        <Typography align="right">{formatCurrency(item.Total?.Amount?.ExTax)}</Typography>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Paper>
                                    </Box>
                                ))}
                            </Box>
                        ))
                    ) : (
                        <Typography variant="body1" color="text.secondary" sx={{ fontStyle: 'italic' }}>No line items available for this quote.</Typography>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={3}>
                    {quote.Notes ? (
                        <Box sx={{ p: 2, bgcolor: '#1e1e1e', borderRadius: 1 }} dangerouslySetInnerHTML={{ __html: quote.Notes }} />
                    ) : (
                        <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>No notes available</Typography>
                    )}
                </TabPanel>

                <TabPanel value={tabValue} index={4}>
                    <HistoryTab logs={quote.logs} />
                </TabPanel>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} variant="outlined">Close</Button>
            </DialogActions>
        </Dialog>
    );
}

export default QuoteDetailsModal;
